import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TopNav from './TopNav';
import api from '../services/api';
import PipelineStatus from './PipelineStatus';

interface ScreeningResult {
  symbol: string;
  entryScore: number;
  confidence: string;
  action: string;
  regime: string;
  riskTolerance: string;
  timestamp: string;
  inTop50: boolean; // Legacy field name maintained for API compatibility
}

interface ScreeningStatus {
  scheduler: {
    isRunning: boolean;
    nextRunAt: string | null;
    lastRunAt: string | null;
    lastRunStatus: string;
    scheduledJobCount: number;
    totalJobCount: number;
  };
  tradingUniverseSchedule?: {
    lastBuildTime: string | null;
    nextBuildTime: string | null;
  };
  tradingUniverse: {
    totalStocks: number;
    regime: string;
    lastUpdated: string;
    stickySeats: number;
    freshSeats: number;
  } | null;
  recentActivity: {
    entryScoresLast24h: number;
  };
}

interface ScreeningSummary {
  totalScreened: number;
  buySignals: number;
  holdSignals: number;
  inTop50: number; // Legacy field name maintained for API compatibility
  timeRange: {
    start: string;
    end: string;
  };
}

const MarketScreening: React.FC = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState<ScreeningStatus | null>(null);
  const [results, setResults] = useState<ScreeningResult[]>([]);
  const [summary, setSummary] = useState<ScreeningSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'status' | 'results' | 'universe' | 'pipeline'>(() => {
    const savedTab = localStorage.getItem('marketScreeningTab');
    return (savedTab as 'status' | 'results' | 'universe' | 'pipeline') || 'status';
  });
  const [sortBy, setSortBy] = useState<'entryScore' | 'symbol' | 'timestamp'>('entryScore');
  const [filterBy, setFilterBy] = useState<'all' | 'buy' | 'hold' | 'universe'>('all');
  const [triggering, setTriggering] = useState(false);
  const [rebuildingUniverse, setRebuildingUniverse] = useState(false);

  // Persist active tab to localStorage
  useEffect(() => {
    localStorage.setItem('marketScreeningTab', activeTab);
  }, [activeTab]);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/market-screening/status');
      setStatus(response.data.data);
    } catch (err) {
      console.error('Failed to fetch market screening status:', err);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await api.get('/market-screening/results', {
        params: { limit: 100, hours: 24 }
      });
      setResults(response.data.data.results);
      setSummary(response.data.data.summary);
    } catch (err) {
      console.error('Failed to fetch market screening results:', err);
      setError('Failed to load market screening results');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchStatus(), fetchResults()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleTriggerScreening = async () => {
    if (!user || user.role !== 'ADMIN') {
      return;
    }

    setTriggering(true);
    setError(null);
    try {
      const response = await api.post('/market-screening/trigger', {
        testMode: false,
        reason: 'manual_trigger'
      });
      
      // Show success message
      if (response.data.success) {
        alert('✅ Market screening triggered successfully! Results will appear in 10-20 seconds.');
      }
      
      setTimeout(() => {
        fetchData();
        setTriggering(false);
      }, 2000);
    } catch (err: any) {
      console.error('Failed to trigger market screening:', err);
      const errorMsg = err?.response?.data?.error?.message || err?.response?.data?.error || 'Failed to trigger market screening';
      setError(errorMsg);
      alert(`❌ Failed to trigger screening: ${errorMsg}`);
      setTriggering(false);
    }
  };

  const handleRebuildUniverse = async () => {
    if (!user || user.role !== 'ADMIN') {
      return;
    }

    setRebuildingUniverse(true);
    setError(null);
    try {
      const response = await api.post('/market-screening/trigger-universe-rebuild', {
        reason: 'manual_admin_rebuild'
      });
      
      // Show success message
      if (response.data.success) {
        alert('✅ Trading Universe rebuild started! This will take 2-3 minutes. The page will refresh automatically.');
      }
      
      // Universe rebuild takes 2-3 minutes, so we'll refresh after 3 minutes
      setTimeout(() => {
        fetchData();
        setRebuildingUniverse(false);
      }, 180000); // 3 minutes
      // Also do an intermediate refresh after 30 seconds to show it's in progress
      setTimeout(() => {
        fetchData();
      }, 30000);
    } catch (err: any) {
      console.error('Failed to trigger universe rebuild:', err);
      const errorMsg = err?.response?.data?.error?.message || err?.response?.data?.error || 'Failed to trigger universe rebuild';
      setError(`${errorMsg}. Check if market is open (9:45 AM - 3:30 PM ET)`);
      alert(`❌ Failed to rebuild universe: ${errorMsg}`);
      setRebuildingUniverse(false);
    }
  };

  const getRegimeColor = (regime: string) => {
    switch (regime) {
      case 'risk_on': return 'text-green-400';
      case 'risk_off': return 'text-red-400';
      case 'neutral': return 'text-yellow-400';
      default: return 'text-neutral-400';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'BUY': return 'text-green-400';
      case 'HOLD': return 'text-yellow-400';
      case 'SELL': return 'text-red-400';
      default: return 'text-neutral-400';
    }
  };

  const filteredResults = results
    .filter(result => {
      if (filterBy === 'buy') return result.action === 'BUY';
      if (filterBy === 'hold') return result.action === 'HOLD';
      if (filterBy === 'universe') return result.inTop50;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'entryScore':
          return b.entryScore - a.entryScore;
        case 'symbol':
          return a.symbol.localeCompare(b.symbol);
        case 'timestamp':
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            <span className="ml-3 text-neutral-400">Loading Market Screening...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Market Screening</h1>
                <p className="text-neutral-400">
                  Real-time stock screening with Trading Universe integration
                </p>
              </div>
              {user?.role === 'ADMIN' && (
                <div className="flex flex-col gap-2">
                  <div className="flex gap-3">
                    <button
                      onClick={handleRebuildUniverse}
                      disabled={rebuildingUniverse}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                      title="Clears pipeline cache and rebuilds Trading Universe (2-3 min)"
                      aria-label="Rebuild Trading Universe without cache"
                    >
                      {rebuildingUniverse ? 'Rebuilding...' : 'Rebuild Universe (no cache)'}
                    </button>
                    <button
                      onClick={handleTriggerScreening}
                      disabled={triggering}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                      {triggering ? 'Triggering...' : 'Trigger Screening'}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-neutral-400">
                    <span>
                      Rebuild (clears cache each time). Last: {status?.tradingUniverseSchedule?.lastBuildTime
                        ? new Date(status.tradingUniverseSchedule.lastBuildTime).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET'
                        : 'Never'}
                      {status?.tradingUniverseSchedule?.nextBuildTime && (
                        <> | Next {new Date(status.tradingUniverseSchedule.nextBuildTime).toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' })} ET</>
                      )}
                    </span>
                    <span>
                      Trigger Screening: Last {status?.scheduler?.lastRunAt
                        ? new Date(status.scheduler.lastRunAt).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET'
                        : 'Never'}
                      {status?.scheduler?.nextRunAt && (
                        <> | Next {new Date(status.scheduler.nextRunAt).toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' })} ET</>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          {status && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-neutral-800 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-400">Scheduler Status</p>
                    <p className={`text-2xl font-bold ${status.scheduler.isRunning ? 'text-green-400' : 'text-red-400'}`}>
                      {status.scheduler.isRunning ? 'Running' : 'Stopped'}
                    </p>
                  </div>
                  <div className="text-2xl">
                    {status.scheduler.isRunning ? '✓' : '✗'}
                  </div>
                </div>
              </div>

              {status.tradingUniverse && (
                <>
                  <div className="bg-neutral-800 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-neutral-400">Universe Stocks</p>
                        <p className="text-2xl font-bold text-white">{status.tradingUniverse.totalStocks}</p>
                      </div>
                      <div className="text-2xl">📊</div>
                    </div>
                  </div>

                  <div className="bg-neutral-800 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-neutral-400">Market Regime</p>
                        <p className={`text-xl font-bold ${getRegimeColor(status.tradingUniverse.regime)}`}>
                          {status.tradingUniverse.regime.replace('_', ' ').toUpperCase()}
                        </p>
                      </div>
                      <div className="text-2xl">🌊</div>
                    </div>
                  </div>
                </>
              )}

              <div className="bg-neutral-800 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-400">Scores (24h)</p>
                    <p className="text-2xl font-bold text-white">{status.recentActivity.entryScoresLast24h}</p>
                  </div>
                  <div className="text-2xl">📈</div>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('status')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'status'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Status
              </button>
              <button
                onClick={() => setActiveTab('results')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'results'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Screening Results
              </button>
              <button
                onClick={() => setActiveTab('universe')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'universe'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Universe Integration
              </button>
              <button
                onClick={() => setActiveTab('pipeline')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'pipeline'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Pipeline Status
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'status' && status && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Screening Status</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-neutral-700 rounded-lg p-4">
                    <h4 className="text-sm text-neutral-400 mb-2">Last Run</h4>
                    <p className="text-lg text-white">
                      {status.scheduler.lastRunAt 
                        ? new Date(status.scheduler.lastRunAt).toLocaleString()
                        : 'Never'}
                    </p>
                    {status.scheduler.lastRunStatus && (
                      <p className={`text-sm mt-1 ${
                        status.scheduler.lastRunStatus === 'completed' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        Status: {status.scheduler.lastRunStatus}
                      </p>
                    )}
                  </div>

                  <div className="bg-neutral-700 rounded-lg p-4">
                    <h4 className="text-sm text-neutral-400 mb-2">Next Run</h4>
                    <p className="text-lg text-white">
                      {status.scheduler.nextRunAt 
                        ? new Date(status.scheduler.nextRunAt).toLocaleString()
                        : 'Not scheduled'}
                    </p>
                  </div>
                </div>

                {status.tradingUniverse && (
                  <div className="bg-neutral-700 rounded-lg p-4">
                    <h4 className="text-sm text-neutral-400 mb-2">Trading Universe</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                      <div>
                        <p className="text-xs text-neutral-400">Total Stocks</p>
                        <p className="text-lg font-bold text-white">{status.tradingUniverse.totalStocks}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-400">Sticky Seats</p>
                        <p className="text-lg font-bold text-blue-400">{status.tradingUniverse.stickySeats}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-400">Fresh Seats</p>
                        <p className="text-lg font-bold text-green-400">{status.tradingUniverse.freshSeats}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-400">Last Updated</p>
                        <p className="text-sm text-white">
                          {new Date(status.tradingUniverse.lastUpdated).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'results' && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Screening Results</h3>
                {summary && (
                  <div className="text-sm text-neutral-400">
                    {summary.totalScreened} stocks • {summary.buySignals} BUY • {summary.holdSignals} HOLD
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Sort by</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="bg-neutral-700 text-white px-3 py-2 rounded-lg border border-neutral-600"
                  >
                    <option value="entryScore">Entry Score</option>
                    <option value="symbol">Symbol</option>
                    <option value="timestamp">Timestamp</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Filter by</label>
                  <select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value as any)}
                    className="bg-neutral-700 text-white px-3 py-2 rounded-lg border border-neutral-600"
                  >
                    <option value="all">All Results</option>
                    <option value="buy">BUY Signals</option>
                    <option value="hold">HOLD Signals</option>
                    <option value="universe">Universe Stocks</option>
                  </select>
                </div>
              </div>

              {/* Results Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-neutral-300 border-b border-neutral-700">
                      <th className="py-3 pr-4">Symbol</th>
                      <th className="py-3 pr-4">Entry Score</th>
                      <th className="py-3 pr-4">Action</th>
                      <th className="py-3 pr-4">Confidence</th>
                      <th className="py-3 pr-4">Regime</th>
                      <th className="py-3 pr-4">In Universe</th>
                      <th className="py-3 pr-4">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-700">
                    {filteredResults.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-neutral-400">
                          No screening results found
                        </td>
                      </tr>
                    ) : (
                      filteredResults.map((result) => (
                        <tr key={`${result.symbol}-${result.timestamp}`} className="hover:bg-neutral-700/50">
                          <td className="py-3 pr-4">
                            <div className="font-semibold text-white">{result.symbol}</div>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center">
                              <div className="w-16 bg-neutral-600 rounded-full h-2 mr-2">
                                <div
                                  className="bg-cyan-400 h-2 rounded-full"
                                  style={{ width: `${result.entryScore * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-white">{(result.entryScore * 100).toFixed(1)}%</span>
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`font-semibold ${getActionColor(result.action)}`}>
                              {result.action}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-neutral-300">{result.confidence}</td>
                          <td className="py-3 pr-4">
                            <span className={getRegimeColor(result.regime)}>
                              {result.regime.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            {result.inTop50 ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-green-900/30 text-green-400">
                                Yes
                              </span>
                            ) : (
                              <span className="text-neutral-500">No</span>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-neutral-400 text-xs">
                            {new Date(result.timestamp).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'universe' && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Trading Universe Integration</h3>
              <p className="text-neutral-400 mb-4">
                Market screening uses the Trading Universe to identify the best stocks for evaluation.
                The universe is updated regularly based on market regime and factor scores.
              </p>
              
              {summary && (
                <div className="bg-neutral-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-3">Screening Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-neutral-400">Total Screened</p>
                      <p className="text-2xl font-bold text-white">{summary.totalScreened}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-400">In Universe</p>
                      <p className="text-2xl font-bold text-cyan-400">{summary.inTop50}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-400">BUY Signals</p>
                      <p className="text-2xl font-bold text-green-400">{summary.buySignals}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-400">HOLD Signals</p>
                      <p className="text-2xl font-bold text-yellow-400">{summary.holdSignals}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <a
                  href="/admin/trading-universe"
                  className="inline-flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
                >
                  View Trading Universe
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          )}

          {activeTab === 'pipeline' && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Screening Pipeline Status</h3>
              <p className="text-neutral-400 mb-6">
                Real-time visibility into trading universe and market screening pipelines. 
                See stage-by-stage progress, conversion rates, and any errors.
              </p>
              <PipelineStatus />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketScreening;
